import {
  integrationService,
  reportService,
  statusService,
} from "/app/src/services";
import FieldsDataHook from "./fields/dataHook";
import { Integration } from "/app/src/models";
import { buildParams } from "/app/src/helpers/params";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { handlePromiseError } from "/app/src/helpers/api";
import { useCallback } from "react";
import useSetting from "/app/src/components/settings/setting";
import { getChangedValues } from "/app/src/helpers/forms";
import {
  convertBooleanToString,
  convertStringToBoolean,
} from "/app/src/helpers";

interface GeneralSettingsFormValues {
  number?: string | number;
  token?: string;
  reportId?: number;
  creationMode?: string;
  values?: (string | number)[];
  value?: boolean;
  name?: string;
}
/**
 * DataHook for the settings of the integration
 */
export default function DataHook(integration: Integration) {
  const queryClient = useQueryClient();

  const { mutateAsync: updateIntegration } = useMutation({
    mutationFn: (values: Integration) => {
      return integrationService
        .updateSingle(integration.id, values)
        .then(handlePromiseError);
    },
    onSuccess: (response) => {
      queryClient.setQueryData(
        ["integration", Number(integration.id)],
        response,
      );
    },
  });
  const {
    settingValue: creationMode,
    createUpdateSetting: createUpdateCreationMode,
  } = useSetting({
    integrationId: integration.id,
    type: "creationMode",
  });
  const {
    settingValue: internalMappingSettingValue,
    createUpdateSetting: createUpdateInternalMapping,
  } = useSetting({
    integrationId: integration.id,
    type: "internalNameMapping",
  });
  const { data: statuses } = useQuery({
    queryKey: ["statuses"],
    queryFn: () =>
      statusService.getAll(
        buildParams({ integrationId: integration.id, includeDefaults: true }),
      ),
    select: (data) => data.statuses,
    initialData: { statuses: [] },
  });
  const {
    settingValue: autoSendSettingValue,
    createUpdateSetting: createUpdateAutoSend,
  } = useSetting({
    integrationId: integration.id,
    type: "autoSend",
  });

  const handleSubmit = useCallback(
    async (values: GeneralSettingsFormValues) => {
      const changedIntegrationValues = getChangedValues(
        {
          name: values.name,
          number: values.number,
          token: values.token,
          reportId: values.reportId,
        },
        integration,
        ["name", "number", "token", "reportId"],
      );
      if (Object.keys(changedIntegrationValues).length) {
        await updateIntegration(changedIntegrationValues);
      }
      if (values.creationMode !== creationMode) {
        await createUpdateCreationMode({
          value: values.creationMode,
          type: "creationMode",
          integrationId: integration.id,
        });
      }
      if (values.values?.join(";") !== internalMappingSettingValue) {
        await createUpdateInternalMapping({
          value: values.values.join(";"),
          type: "internalNameMapping",
          integrationId: integration.id,
        });
      }
      if (values.value !== convertStringToBoolean(autoSendSettingValue)) {
        await createUpdateAutoSend({
          value: convertBooleanToString(values.value),
          type: "autoSend",
          integrationId: integration.id,
        });
      }
    },
    [
      autoSendSettingValue,
      createUpdateAutoSend,
      createUpdateCreationMode,
      createUpdateInternalMapping,
      creationMode,
      integration,
      internalMappingSettingValue,
      updateIntegration,
    ],
  );
  const { data: reports } = useQuery({
    queryKey: [
      "reports",
      "[or]LocationContentBreakdown;LocationContent;History;Material",
    ],
    queryFn: () =>
      reportService.getAll(
        buildParams({
          baseTable:
            "[or]LocationContentBreakdown;LocationContent;History;Material",
        }),
      ),
    initialData: { reports: [] },
    select: (data) => data.reports,
  });

  // Use fields data hook to get line/order fields and delete function
  const { lineFields, orderFields, deleteField } = FieldsDataHook(integration);

  // Function to delete all fields (one API call per field)
  const deleteAllFields = useCallback(async () => {
    const all = [...(lineFields || []), ...(orderFields || [])];
    if (all.length === 0) {
      return; // No fields to delete
    }

    const deletePromises = all.map((field) =>
      deleteField(field).catch((err) => Promise.reject(err)),
    );
    const results = await Promise.allSettled(deletePromises);

    // Check if any deletions failed
    const failures = results.filter((r) => r.status === "rejected");
    if (failures.length > 0) {
      const failedFieldIds = all
        .filter((_, index) => results[index].status === "rejected")
        .map((field) => field.id)
        .join(", ");
      // Re-throw error with details
      throw new Error(
        `Failed to delete ${failures.length} of ${all.length} fields (IDs: ${failedFieldIds})`,
      );
    }

    // Ensure queries are fresh
    queryClient.invalidateQueries(["lineFields", integration.id]);
    queryClient.invalidateQueries(["orderFields", integration.id]);
  }, [lineFields, orderFields, deleteField, queryClient, integration.id]);

  // Function to reset internal name mapping
  const resetInternalMapping = useCallback(async () => {
    await createUpdateInternalMapping({
      value: "",
      type: "internalNameMapping",
      integrationId: integration.id,
    });
  }, [createUpdateInternalMapping, integration.id]);

  return {
    reports,
    handleSubmit,
    internalMappingSettingValue,
    autoSendSettingValue,
    creationMode,
    hasCustomStatuses: statuses.some((status) => status.type === "custom"),
    deleteAllFields,
    resetInternalMapping,
  };
}
